import { useState, type ReactNode } from 'react';
import { API_BASE } from '@/api/client';

/**
 * Generic admin-key gate — same PasswordGate UI/UX already used in
 * AdminResponses.tsx, but self-contained: it validates the typed key
 * itself against GET /api/v1/admin/verify-key (server-side check against
 * ADMIN_API_KEY, never stored in the bundle) and only then renders
 * `children`. Used to gate the old landing page at its new route once the
 * revamp became the real "/".
 */
export default function AdminKeyGate({ children }: { children: ReactNode }) {
  const [unlocked, setUnlocked] = useState(false);
  const [value, setValue] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  const attempt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setChecking(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/verify-key`, {
        headers: { 'X-Admin-Key': value },
      });
      if (res.ok) {
        setUnlocked(true);
      } else {
        setError('Invalid admin key.');
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
    } catch {
      setError('Could not reach the server — try again.');
    } finally {
      setChecking(false);
    }
  };

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-screen bg-[#04070B] flex items-center justify-center px-4">
      <div className={`w-full max-w-sm ${shake ? 'animate-shake' : ''}`}>
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-white text-xl font-bold mb-1">Admin Access</h1>
          <p className="text-gray-500 text-sm">Enter the admin password to continue</p>
        </div>
        <form onSubmit={attempt} className="space-y-3">
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Password"
            autoFocus
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-white/25 transition-colors"
          />
          <button
            type="submit"
            disabled={checking}
            className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50"
          >
            {checking ? 'Checking…' : 'Unlock'}
          </button>
          {error && <p className="text-red-400 text-xs text-center">{error}</p>}
        </form>
      </div>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-8px); }
          80% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}
